import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import {
  Table2, Users, Clock, CheckCircle2, CircleDot, Maximize, Minimize,
  RefreshCw, ShoppingCart, ChefHat, Wifi, WifiOff
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  available: { color: '#10B981', bg: '#10B98118', border: '#10B98140', label_ar: 'متاحة', label_en: 'Available', icon: CheckCircle2 },
  occupied: { color: '#EF4444', bg: '#EF444418', border: '#EF444440', label_ar: 'مشغولة', label_en: 'Occupied', icon: CircleDot },
  reserved: { color: '#F59E0B', bg: '#F59E0B18', border: '#F59E0B40', label_ar: 'محجوزة', label_en: 'Reserved', icon: Clock },
};

function useTimer() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatDuration(startIso, now) {
  if (!startIso) return '';
  const diff = now - new Date(startIso).getTime();
  if (diff < 0) return '';
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const m = mins % 60;
  if (hrs > 0) return `${hrs}:${String(m).padStart(2, '0')}`;
  return `${m} د`;
}

export default function LiveTablesDashboard() {
  const { authHeaders, activeBranch, token } = useAuth();
  const { t } = useTranslation();
  const { lang, dir } = useLanguage();
  const isAr = lang === 'ar';
  const now = useTimer();

  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const wsRef = useRef(null);
  const containerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!activeBranch || !token) return;
    try {
      const [tablesR, statsR] = await Promise.all([
        axios.get(`${API}/api/tables?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/tables/stats?branch_id=${activeBranch.id}`, authHeaders()),
      ]);
      setTables(tablesR.data);
      setStats(statsR.data);
      setLastRefresh(Date.now());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [activeBranch, token, authHeaders]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!activeBranch) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '') + `/api/ws/kitchen/${activeBranch.id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);
      ws._ping = ping;
    };

    ws.onmessage = () => { fetchData(); };
    ws.onclose = () => {
      setWsConnected(false);
      if (ws._ping) clearInterval(ws._ping);
      setTimeout(() => { if (wsRef.current === ws) fetchData(); }, 3000);
    };
    ws.onerror = () => ws.close();

    return () => { ws.close(); };
  }, [activeBranch, fetchData]);

  // Poll every 15s as fallback
  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
  }, [fetchData]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const available = tables.filter(t => (t.status || 'available') === 'available').length;
  const occupied = tables.filter(t => (t.status || 'available') === 'occupied').length;
  const reserved = tables.filter(t => (t.status || 'available') === 'reserved').length;

  return (
    <div ref={containerRef} dir={dir} data-testid="live-tables-dashboard"
      className="min-h-screen flex flex-col"
      style={{ background: '#0A0A0F', fontFamily: "'Cairo', 'Tajawal', sans-serif" }}>

      {/* Top Bar */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <ChefHat className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-white" data-testid="live-title">
              {isAr ? 'خريطة الطاولات الحية' : 'Live Table Map'}
            </h1>
            <p className="text-[10px] text-white/30">{activeBranch?.name || ''}</p>
          </div>
        </div>

        {/* Stats pills */}
        <div className="hidden sm:flex items-center gap-3">
          {[
            { label: isAr ? 'متاحة' : 'Available', value: available, color: STATUS.available.color },
            { label: isAr ? 'مشغولة' : 'Occupied', value: occupied, color: STATUS.occupied.color },
            { label: isAr ? 'محجوزة' : 'Reserved', value: reserved, color: STATUS.reserved.color },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full"
              style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
              <div className="w-2 h-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 6px ${s.color}` }} />
              <span className="text-xs font-bold" style={{ color: s.color }}>{s.value}</span>
              <span className="text-[10px] text-white/40">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${wsConnected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}
            data-testid="live-ws-status">
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'LIVE' : 'OFFLINE'}
          </div>
          <button onClick={fetchData} className="p-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
            data-testid="live-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={toggleFullscreen} className="p-2 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
            data-testid="live-fullscreen">
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Mobile stats */}
      <div className="flex sm:hidden items-center justify-center gap-4 py-2 px-4"
        style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
        {[
          { value: available, color: STATUS.available.color, label: isAr ? 'متاحة' : 'Free' },
          { value: occupied, color: STATUS.occupied.color, label: isAr ? 'مشغولة' : 'Busy' },
          { value: reserved, color: STATUS.reserved.color, label: isAr ? 'محجوزة' : 'Rsv' },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
            <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
            <span className="text-[10px] text-white/30">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Tables Grid */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
              <p className="text-white/30 text-sm">{isAr ? 'جاري التحميل...' : 'Loading...'}</p>
            </div>
          </div>
        ) : tables.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Table2 className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <p className="text-white/30">{t('liveTables.noTables')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5"
            data-testid="live-tables-grid">
            {tables.map(table => {
              const s = STATUS[table.status] || STATUS.available;
              const hasOrder = table.active_order;
              const orderTime = hasOrder ? table.active_order.created_at : null;
              const duration = formatDuration(orderTime, now);
              const isLong = orderTime && (now - new Date(orderTime).getTime()) > 45 * 60000; // >45min

              return (
                <div key={table.id} data-testid={`live-table-${table.table_number}`}
                  className="relative rounded-2xl overflow-hidden transition-all duration-500"
                  style={{
                    background: s.bg, border: `2px solid ${s.border}`,
                    boxShadow: hasOrder ? `0 0 20px ${s.color}15, inset 0 0 30px ${s.color}08` : 'none',
                    animation: hasOrder && table.status === 'occupied' ? 'none' : undefined,
                  }}>
                  {/* Glow effect for occupied */}
                  {table.status === 'occupied' && (
                    <div className="absolute inset-0 pointer-events-none rounded-2xl"
                      style={{ background: `radial-gradient(circle at center, ${s.color}08, transparent 70%)` }} />
                  )}

                  <div className="relative p-4 sm:p-5 text-center">
                    {/* Status dot */}
                    <div className="absolute top-3 end-3">
                      <div className="w-3 h-3 rounded-full"
                        style={{
                          background: s.color,
                          boxShadow: `0 0 8px ${s.color}80`,
                          animation: table.status === 'occupied' ? 'pulse 2s ease-in-out infinite' : 'none',
                        }} />
                    </div>

                    {/* Table number - large */}
                    <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-2xl flex items-center justify-center mb-3"
                      style={{ background: `${s.color}20`, border: `2px solid ${s.color}40` }}>
                      <span className="text-2xl sm:text-3xl font-black" style={{ color: s.color }}>
                        {table.table_number}
                      </span>
                    </div>

                    {/* Label */}
                    <p className="text-sm font-bold text-white/90 mb-1">{table.label}</p>

                    {/* Seats */}
                    <div className="flex items-center justify-center gap-1 text-white/30 mb-2">
                      <Users className="w-3 h-3" />
                      <span className="text-xs">{table.seats}</span>
                    </div>

                    {/* Timer for occupied */}
                    {hasOrder && (
                      <div className="mt-2 space-y-1.5" data-testid={`live-timer-${table.table_number}`}>
                        <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl"
                          style={{
                            background: isLong ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)',
                            border: `1px solid ${isLong ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.08)'}`,
                          }}>
                          <Clock className="w-3 h-3" style={{ color: isLong ? '#EF4444' : 'rgba(255,255,255,0.4)' }} />
                          <span className="text-sm font-bold" style={{ color: isLong ? '#FCA5A5' : 'rgba(255,255,255,0.6)', fontFeatureSettings: '"tnum"' }}>
                            {duration}
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <ShoppingCart className="w-3 h-3 text-white/30" />
                          <span className="text-xs font-bold" style={{ color: s.color }}>
                            {table.active_order.total_price} {t('common.sar')}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Status label */}
                    {!hasOrder && (
                      <div className="mt-2">
                        <span className="text-[11px] font-semibold px-3 py-1 rounded-full"
                          style={{ background: `${s.color}15`, color: s.color }}>
                          {isAr ? s.label_ar : s.label_en}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Bottom bar */}
      <footer className="flex items-center justify-between px-4 sm:px-6 py-2"
        style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.01)' }}>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-white/20">
            {isAr ? `${tables.length} طاولة | ${stats?.total_seats || 0} مقعد` : `${tables.length} tables | ${stats?.total_seats || 0} seats`}
          </span>
          {stats?.today_dinein_orders > 0 && (
            <span className="text-[10px] text-amber-500/60">
              {isAr ? `${stats.today_dinein_orders} طلب اليوم` : `${stats.today_dinein_orders} orders today`}
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/15">
          {isAr ? 'آخر تحديث:' : 'Last update:'} {new Date(lastRefresh).toLocaleTimeString(isAr ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      </footer>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }
      `}</style>
    </div>
  );
}
